import { Map } from 'immutable';
import React, { useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { routes } from 'flow/types/routes';
import { PbxParams } from 'flow/types/Params';
import Page from 'flow/components/Setting/Page';
import { twMerge } from 'tailwind-merge';
import { selectOwnUserHasCapability } from 'flow/selectors/ExtensionSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import Toggle from 'flow/components/ui-components/Toggle';
import SettingRow from 'flow/components/Setting/SettingRow';
import Divider from 'flow/components/ui-components/Divider';
import SettingsListItem from 'flow/components/custom-components/SettingsListItem';
import {
    fetchSharedVoicemail,
    updateSharedVoicemail,
} from 'flow/actions/pbx-actions';
import { Capabilities } from 'flow/utils/capability-utils';

const SharedVoicemailSettings = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { entityKey } = useParams<PbxParams>();
    const ownUserHasCapabilitySms = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.SMS),
    );

    useEffect(() => {
        const intervalID = setInterval(() => {
            dispatch(fetchSharedVoicemail(entityKey, 'contact'));
        }, 30000);

        return () => clearInterval(intervalID);
    }, [dispatch, entityKey]);

    const voicemailDTO = useAppSelector((state) =>
        state.pbx.getIn(['voicemails', entityKey], Map()),
    );

    return (
        <Page
            header={i18next.t('settings')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.voicemails.entityKey'], {
                        entityKey,
                    }),
                )
            }
        >
            <SettingsListItem
                editable={true}
                description={voicemailDTO.get('description')}
                to={generatePath(
                    routes['pbx.voicemails.entityKey.settings.edit.name'],
                    { entityKey },
                )}
            >
                {i18next.t('pbx.name')}
            </SettingsListItem>

            <Divider />

            <SettingsListItem
                editable={true}
                description={voicemailDTO.getIn([
                    'settings',
                    'busySound',
                    'name',
                ])}
                to={generatePath(
                    routes['pbx.voicemails.voicemailKey.settings.sound'],
                    { voicemailKey: entityKey },
                )}
            >
                {i18next.t('pbx.sound')}
            </SettingsListItem>

            <Divider />

            {ownUserHasCapabilitySms && (
                <>
                    <SettingRow
                        cta={
                            <Toggle
                                value={voicemailDTO.getIn([
                                    'settings',
                                    'noticeSMS',
                                ])}
                                onChange={(status) =>
                                    dispatch(
                                        updateSharedVoicemail(
                                            entityKey,
                                            voicemailDTO
                                                .updateIn(
                                                    ['settings', 'noticeSMS'],
                                                    () => status,
                                                )
                                                .delete('voiceMessages'),
                                        ),
                                    )
                                }
                                text={i18next.t('pbx.notice.mobile')}
                            />
                        }
                    >
                        {i18next.t('pbx.notice.mobile')}
                    </SettingRow>

                    <SettingsListItem
                        className={twMerge(
                            voicemailDTO.getIn(['settings', 'noticeSMS'])
                                ? 'text-gray-900'
                                : 'text-gray-500',
                        )}
                        editable={voicemailDTO.getIn(['settings', 'noticeSMS'])}
                        description={voicemailDTO.getIn([
                            'settings',
                            'mobileNumber',
                        ])}
                        to={generatePath(
                            routes[
                                'pbx.voicemails.entityKey.settings.edit.number'
                            ],
                            { entityKey },
                        )}
                    >
                        {i18next.t('pbx.number')}
                    </SettingsListItem>

                    <div className="px-2 text-xs text-gray-500">
                        {i18next.t('pbx.notice.mobile.charge')}
                    </div>

                    <Divider />
                </>
            )}

            <SettingRow
                cta={
                    <Toggle
                        value={voicemailDTO.getIn(['settings', 'noticeEmail'])}
                        onChange={(status) =>
                            dispatch(
                                updateSharedVoicemail(
                                    entityKey,
                                    voicemailDTO
                                        .updateIn(
                                            ['settings', 'noticeEmail'],
                                            () => status,
                                        )
                                        .delete('voiceMessages'),
                                ),
                            )
                        }
                        text={i18next.t('pbx.notice.email')}
                    />
                }
            >
                {i18next.t('pbx.notice.email')}
            </SettingRow>

            <SettingsListItem
                className={twMerge(
                    voicemailDTO.getIn(['settings', 'noticeEmail'])
                        ? 'text-gray-900'
                        : 'text-gray-500',
                )}
                editable={voicemailDTO.getIn(['settings', 'noticeEmail'])}
                description={voicemailDTO.getIn(['settings', 'email'])}
                to={generatePath(
                    routes['pbx.voicemails.entityKey.settings.edit.email'],
                    { entityKey },
                )}
            >
                {i18next.t('pbx.email')}
            </SettingsListItem>

            <div className="px-2 text-xs text-gray-500">
                {i18next.t('pbx.notice.attach.sound')}
            </div>
        </Page>
    );
};

export default SharedVoicemailSettings;
